import ApiClient from './ApiClient';

const mapMessageType = type => {
  const typeMap = {
    0: 'incoming',
    1: 'outgoing',
    2: 'activity',
    3: 'template',
  };
  return typeMap[type] || 'incoming';
};

const mapSenderType = sender => {
  if (!sender) return null;
  const type = sender.type?.toLowerCase() || '';
  if (type.includes('contact')) return 'contact';
  if (type.includes('user')) return 'user';
  if (type.includes('bot')) return 'bot';
  return null;
};

async function* createSSEConnection(url, body) {
  let currentEventType = 'message';

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    credentials: 'include',
    body,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-restricted-syntax
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          const data = line.slice(5).trim();
          if (data) {
            try {
              const parsed = JSON.parse(data);
              yield {
                event: currentEventType,
                data: parsed,
              };
            } catch {
              yield {
                event: currentEventType,
                data: { raw: data },
              };
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

class AgentsSidecarAPI extends ApiClient {
  constructor() {
    super('agents_sidecar', { accountScoped: true });
  }

  /**
   * Analyze a conversation and get AI suggestions
   * Returns an async generator for SSE streaming
   * @param {number} conversationId
   * @param {Array} messages
   * @param {string} requestType - 'full_analysis' | 'quick_summary' | 'draft_reply'
   * @returns {AsyncGenerator}
   */
  async *analyzeStream(
    conversationId,
    messages,
    requestType = 'full_analysis'
  ) {
    const url = `${this.url}/analyze`;
    const body = JSON.stringify({
      conversation_id: conversationId,
      messages: messages.map(m => ({
        id: m.id,
        content: m.content,
        content_type: m.content_type || 'text',
        message_type: mapMessageType(m.message_type),
        sender_type: mapSenderType(m.sender),
        sender_name: m.sender?.name,
        created_at: m.created_at,
        private: m.private || false,
        attachments: m.attachments || [],
      })),
      request_type: requestType,
    });

    yield* createSSEConnection(url, body);
  }

  /**
   * Query across conversations using global knowledge
   * Returns an async generator for SSE streaming
   * @param {string} query
   * @param {number|null} inboxId
   * @param {number|null} conversationId
   * @param {Array} messages - Conversation messages for context
   * @param {Array} chatHistory - Previous AI Q&A exchanges
   * @returns {AsyncGenerator}
   */
  async *queryStream(
    query,
    inboxId = null,
    conversationId = null,
    messages = [],
    threadId = null
  ) {
    const url = `${this.url}/query`;
    const body = JSON.stringify({
      query,
      inbox_id: inboxId,
      conversation_id: conversationId,
      messages: messages.map(m => ({
        id: m.id,
        content: m.content,
        content_type: m.content_type || 'text',
        message_type: mapMessageType(m.message_type),
        sender_type: mapSenderType(m.sender),
        sender_name: m.sender?.name,
        created_at: m.created_at,
        private: m.private || false,
      })),
      thread_id: threadId,
    });

    yield* createSSEConnection(url, body);
  }

  async createThread(conversationId = null, title = null) {
    const url = `${this.url}/threads`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        conversation_id: conversationId,
        title,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async listThreads(conversationId = null) {
    const query = conversationId ? `?conversation_id=${conversationId}` : '';
    const url = `${this.url}/threads${query}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getThreadHistory(threadId) {
    const url = `${this.url}/threads/${threadId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get agent configuration from the sidecar service
   * @returns {Promise<Object>}
   */
  async getConfig() {
    const url = `${this.url}/config`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Update agent configuration
   * @param {Object} configData - Configuration data to update
   * @param {string} configData.llm_provider - LLM provider (google, openai, anthropic)
   * @param {string} configData.llm_model - LLM model name
   * @param {Array} configData.agents - Array of agent configurations
   * @returns {Promise<Object>}
   */
  async updateConfig(configData) {
    const url = `${this.url}/update_config`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(configData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Reset agent configuration to defaults
   * @returns {Promise<Object>}
   */
  async resetConfig() {
    const url = `${this.url}/reset_config`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

export default new AgentsSidecarAPI();
